import clsx from 'clsx'
import { type Ref, computed, defineComponent, onMounted, ref, useId, watch } from 'vue'
import { type JSX } from 'vue/jsx-runtime'

import VIcon from '@/components/common/VIcon'
import useLocalization from '@/composables/localization/useLocalization'
import { stringToCurrencyFormat, stringToNumberString } from '@/utils/number'
import { convertTextFieldErrorMessage } from '@/utils/text-filed'

interface Props {
  tData?: string
  mainRef?: Ref<HTMLElement | null>
  class?: string
  inputClass?: string
  type: 'text' | 'email' | 'number' | 'decimal' | 'password' | 'tel'
  name?: string
  placeholder?: string
  value: string | number
  maxlength?: number
  min?: number
  max?: number
  disabled?: boolean
  autofocus?: boolean
  error?: string | boolean
  description?: string | boolean
  autocomplete?: string
  required?: boolean
  readonly?: boolean
  leftIcon?: string
  isSelecteMode?: boolean
  inputId?: string
  inputmode?: 'text' | 'email' | 'decimal' | 'search' | 'tel' | 'url' | 'none' | 'numeric'
  label?: string
  variant?: 'floating'
  pattern?: string
  onFocus?: (payload: FocusEvent) => void
  onBlur?: (payload: FocusEvent) => void
  onInput?: (value: string, payload: Event) => void
  onClick?: (payload: FocusEvent) => void
  onKeypress?: (payload: KeyboardEvent) => void
  onKeydown?: (payload: KeyboardEvent) => void
  onPaste?: (payload: ClipboardEvent) => void
  onCut?: (payload: ClipboardEvent) => void
}

export default defineComponent((props: Props, context) => {
  const { localeTexts } = useLocalization()
  const componentId = useId()
  const inputElm = ref<HTMLInputElement | null>(null)
  const localErrorMessage = ref<string>()

  // #region Props Getters

  const tData = computed(() => props.tData ?? 'v-text-field')
  const inputId = computed((): string => props.inputId ?? componentId)

  // #endregion

  // #region Getters

  const isFloating = computed((): boolean => props.variant === 'floating')

  const mappedValue = computed((): string | number => {
    return props.type === 'decimal'
      ? stringToCurrencyFormat(`${props.value}`)
      : props.value
  })

  const inputType = computed((): string => (props.type === 'decimal' ? 'text' : props.type))

  const maxlength = computed((): number => (props.maxlength ?? 255))

  const errorMessage = computed((): string | undefined => {
    if (localErrorMessage.value !== undefined) {
      return localErrorMessage.value
    }

    if (typeof props.error === 'string' && props.error.trim() !== '') {
      return props.error
    }

    return undefined
  })

  const isShowDescription = computed((): boolean => errorMessage.value === undefined && (typeof props.description === 'string' && props.description !== ''))

  const hasError = computed((): boolean => props.error === true || errorMessage.value !== undefined)

  const appendCotent = computed((): JSX.Element | undefined => {
    if (context.slots.append === undefined) {
      return
    }

    return (
      <div
        class={clsx(
          'pointer-events-none absolute right-3 top-1/2 flex items-center',
          {
            '-translate-y-1/2': !(!isFloating.value && props.label)
          }
        )}
      >
        {context.slots.append()}
      </div>
    )
  })

  const placeholder = computed((): string | undefined => {
    if (isFloating.value && props.placeholder === undefined) {
      return props.label
    }

    return props.placeholder
  })

  const hasPlaceholder = computed((): boolean => props.placeholder !== undefined)

  // #endregion

  // #region Actions

  const handleInvalid = ():void => {
    localErrorMessage.value = convertTextFieldErrorMessage(
      localeTexts.value,
      inputElm.value?.validationMessage,
      props.value,
      props.name
    )
    inputElm.value?.setCustomValidity('')
  }

  // #endregion

  // #region Watchers

  if (props.isSelecteMode) {
    watch(() => props.value, () => {
      localErrorMessage.value = undefined
    })
  }

  watch(() => props.disabled, () => {
    localErrorMessage.value = undefined
  })

  // #endregion

  onMounted(() => {
    if (props.autofocus) {
      window.setTimeout(() => inputElm.value?.focus(), 0)
    }
  })

  return () => (
    <div
      t-data={tData.value}
      class={['relative', props.class]}
    >
      {!isFloating.value && props.label &&
        <label
          for={inputId.value}
          class={clsx(
            'mb-1 text-subtitle-2',
            {
              'text-neutral': !props.disabled,
              'cursor-pointer': props.isSelecteMode && !props.disabled,
              'text-neutral-400': props.disabled,
              'after:text-subtitle-2 after:text-danger-600 after:content-["*"]': props.required
            }
          )}
        >{props.label}
        </label>}
      <input
        ref={(ref) => {
          inputElm.value = ref as HTMLInputElement

          if (props.mainRef?.value !== undefined) {
            props.mainRef.value = ref as HTMLInputElement
          }
        }}
        id={inputId.value}
        class={clsx([
          'peer text-field',
          hasError.value ? 'text-field-error' : '',
          props.inputClass,
          context.slots.append ? 'text-field-append' : ''
        ], {
          'placeholder:text-transparent disabled:placeholder:text-transparent': isFloating.value && !hasPlaceholder.value,
          'cursor-pointer': props.isSelecteMode && !props.disabled
        })}
        type={inputType.value}
        name={props.name}
        placeholder={placeholder.value}
        value={mappedValue.value}
        maxlength={maxlength.value}
        min={props.min}
        max={props.max}
        disabled={props.disabled}
        autocomplete={props.autocomplete}
        required={props.required}
        readonly={props.readonly}
        inputmode={props.inputmode}
        pattern={props.pattern}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value
          localErrorMessage.value = undefined
          props.onInput?.(props.type === 'decimal' ? stringToNumberString(value) : value, e)
        }}
        onKeypress={props.onKeypress}
        onInvalid={handleInvalid}
        onKeydown={props.onKeydown}
        onPaste={props.onPaste}
        onCut={props.onCut}
        onClick={props.onClick}
      />
      {isFloating.value &&
        <label
          for={inputId.value}
          class={clsx(
            'absolute -top-1.5 left-3 z-0 text-input-label transition-all',
            'before:absolute before:left-0 before:top-1/2 before:-z-10 before:h-1.5 before:w-full before:translate-y-[-1.5px] before:bg-white peer-focus:before:translate-y-[-3px]',
            {
              'text-neutral-500': !props.disabled,
              'cursor-pointer': props.isSelecteMode && !props.disabled,
              'text-neutral-400': props.disabled,
              'pl-1 pr-2': props.required,
              'px-1': !props.required,
              'after:text-subtitle-2 after:text-danger-600 after:content-["*"] after:absolute after:-translate-y-1.5': props.required,
              'peer-placeholder-shown:after:-translate-y-0': props.required && !hasPlaceholder.value,
              'peer-placeholder-shown:top-2 peer-placeholder-shown:px-0 peer-placeholder-shown:text-input': !hasPlaceholder.value,
              'peer-placeholder-shown:before:bg-transparent': !hasPlaceholder.value
            }
          )}
        >{props.label}
        </label>}
      {
        errorMessage.value && (
          <div
            t-data={`${tData.value}-error`}
            class='absolute -bottom-1 left-3 inline-block w-full translate-y-full text-input-label text-danger-600'
          >
            {errorMessage.value}
          </div>)
      }
      {
        isShowDescription.value && (
          <div
            t-data={`${tData.value}-description`}
            class='absolute -bottom-1 left-3 inline-block w-full translate-y-full text-input-label text-neutral-500'
          >
            {props.description}
          </div>)
      }
      {props.leftIcon &&
        <div class='pointer-events-none absolute left-2 top-[7px] h-5.5'>
          <VIcon
            class='text-[20px]'
            icon={props.leftIcon}
          />
        </div>}
      {appendCotent.value}
    </div>
  )
},
{
  name: 'VTextField',
  props: [
    'mainRef',
    'tData',
    'class',
    'inputClass',
    'type',
    'name',
    'placeholder',
    'value',
    'maxlength',
    'min',
    'max',
    'disabled',
    'autofocus',
    'error',
    'description',
    'autocomplete',
    'required',
    'readonly',
    'leftIcon',
    'isSelecteMode',
    'inputId',
    'inputmode',
    'label',
    'variant',
    'pattern',
    'onFocus',
    'onBlur',
    'onClick',
    'onInput',
    'onKeypress',
    'onKeydown',
    'onPaste',
    'onCut'
  ]
})
